import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { AuthorComposerViewStore } from "../../store";
import { AuthorComposerView } from "../view/AuthorComposerView";
import { AuthorComposerUpdateStore } from "../../store";
import { AuthorComposerUpdate } from "../update/AuthorComposerUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type AuthorComposerModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const AuthorComposerModalContent: React.FC<AuthorComposerModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new AuthorComposerUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new AuthorComposerViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <AuthorComposerView
            authorComposerViewStore={storeInfo.store as AuthorComposerViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <AuthorComposerUpdate
            authorComposerUpdateStore={
              storeInfo.store as AuthorComposerUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
